<template>
  <div class="curriculum">
    <div class="is-flex mb-5">
      <CurriculumSelector
        class="is-flex-grow-1"
        :curriculums="curriculums"
        :selectedId="selectedCurriculumId"
        @select="selectedCurriculumId = $event"
      />

      <b-field class="is-align-self-flex-end">
        <b-button
          class="ml-2"
          type="is-warning"
          icon-left="edit"
          :disabled="!selectedCurriculumId"
          @click="editCurriculum"
        />

        <b-button
          class="ml-2"
          label="Curriculum"
          type="is-primary"
          icon-left="plus"
          @click="createCurriculum"
        />
      </b-field>
    </div>

    <div class="columns curriculum-columns">
      <ModulesColumn
        class="column module-column"
        :curriculum="selectedCurriculum"
        :get-topic-by-id="getTopicById"
        :can-edit="true"
        @drop="handleModuleDrop"
        @full="handleModuleFull"
        @clear="handleModuleClear"
        @reorder="handleReorderModules"
        @save="saveCurriculum"
        @editLesson="editLesson"
        @select-topic="handleSelectTopic"
        @remove-topic="handleRemoveTopic"
      />

      <TopicList
        class="column topic-column drop-zone"
        :topics="availableTopics"
        :selectedTopicId="selectedTopicId"
        :topicUsageMap="topicUsageMap"
        :enableDragDrop="true"
        :includeTopicEditor="false"
        @topics-updated="loadTopics"
        @drop="handleTopicsDrop"
        @select-topic="handleSelectTopic"
        @preview-topic="handlePreviewTopic"
      />

      <div class="column">
        <TopicPreview
          :topic="selectedTopic"
          :scenarios="scenarios"
          :canEdit="false"
        />
      </div>
    </div>

    <b-modal
      v-model="isModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      aria-role="dialog"
      aria-modal
    >
      <CurriculumEditor
        :is-editing="isEditing"
        :form-data="formData"
        :is-loading="isLoading"
        @submit="submitForm"
        @close="closeModal"
      />
    </b-modal>

    <b-modal
      v-model="isLessonEditorActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      aria-role="dialog"
      aria-modal
    >
      <LessonEditor
        v-if="lessonToEdit"
        :topic="getTopicById(lessonToEdit.topicId)"
        :isOptional="lessonToEdit.isOptional"
        @close="closeLessonEditor"
        @save="handleLessonSaved"
      />
    </b-modal>
  </div>
</template>

<script>
import { ContentStatus } from "vue-chess-shared";
import { CurriculumService } from "@/services/CoachingServices";
import ModulesColumn from "./ModulesColumn.vue";
import CurriculumSelector from "./CurriculumSelector.vue";
import CurriculumEditor from "./CurriculumEditor.vue";
import LessonEditor from "./LessonEditor.vue";
import TopicPreview from "../explorer/organisms/TopicPreview.vue";
import TopicList from "../explorer/organisms/TopicList.vue";
import topicScenarioLoader from "@/mixins/topicScenarioLoader";

export default {
  name: "Curriculum",

  components: {
    TopicList,
    ModulesColumn,
    TopicPreview,
    CurriculumSelector,
    CurriculumEditor,
    LessonEditor,
  },

  mixins: [topicScenarioLoader],

  data() {
    return {
      curriculums: [],
      selectedCurriculumId: "",
      selectedCurriculum: null,
      topics: [],
      isModalActive: false,
      isLoading: false,
      isEditing: false,
      formData: {
        title: "",
        description: "",
        status: ContentStatus.Draft,
      },
      isLessonEditorActive: false,
      lessonToEdit: null,
    };
  },

  computed: {
    isValid() {
      return this.formData.title.trim().length > 0;
    },

    topicUsageMap() {
      if (!this.selectedCurriculum?.modules) {
        return new Map();
      }

      const mapping = new Map();
      this.selectedCurriculum.modules.forEach((module, index) => {
        module.topicIds.forEach((topicId) => {
          mapping.set(topicId, `w${index + 1}`);
        });
      });
      return mapping;
    },

    sortedTopics() {
      return [...this.topics].sort((a, b) => a.title.localeCompare(b.title));
    },

    availableTopics() {
      if (!this.selectedCurriculum?.lessons) {
        return this.sortedTopics;
      }
      return this.sortedTopics.filter(
        (topic) =>
          !this.selectedCurriculum.lessons.some(
            (lesson) => lesson.topicId === topic.id
          )
      );
    },
  },

  async created() {
    await this.loadCurriculums();
    await this.loadTopics();
  },

  watch: {
    isModalActive(newValue) {
      if (!newValue) {
        this.resetForm();
      }
    },

    selectedCurriculumId(newId) {
      if (newId) {
        this.selectedCurriculum = this.curriculums.find((c) => c.id === newId);
      } else {
        this.selectedCurriculum = null;
      }
    },
  },

  methods: {
    async loadCurriculums() {
      try {
        this.curriculums = await CurriculumService.getCurriculums();
      } catch (error) {
        this.$buefy.toast.open({
          message: `Failed to load curriculums: ${error.message}`,
          type: "is-danger",
        });
      }
    },

    getTopicById(topicId) {
      return this.topics.find((topic) => topic.id === topicId) || null;
    },

    /*
     * CurriculumEditor handlers
     */

    createCurriculum() {
      this.isEditing = false;
      this.formData = {
        title: "",
        description: "",
        status: ContentStatus.Draft,
      };
      this.isModalActive = true;
    },

    editCurriculum() {
      if (!this.selectedCurriculum) return;

      this.isEditing = true;
      this.formData = {
        title: this.selectedCurriculum.title,
        description: this.selectedCurriculum.description || "",
        status: this.selectedCurriculum.status,
      };
      this.isModalActive = true;
    },

    closeModal() {
      this.isLoading = false;
      this.isModalActive = false;
      this.resetForm();
    },

    resetForm() {
      this.formData = {
        title: "",
        description: "",
        status: ContentStatus.Draft,
      };
      this.isEditing = false;
    },

    async submitForm() {
      if (!this.isValid) return;

      try {
        this.isLoading = true;

        if (this.isEditing) {
          await CurriculumService.updateCurriculum(this.selectedCurriculum.id, {
            title: this.formData.title.trim(),
            description: this.formData.description.trim(),
            status: this.formData.status,
          });

          await this.loadCurriculums();

          this.$buefy.toast.open({
            message: "Curriculum updated successfully!",
            type: "is-success",
          });
        } else {
          const curriculum = await CurriculumService.createCurriculum({
            title: this.formData.title.trim(),
            description: this.formData.description.trim(),
            status: this.formData.status,
            lessons: [],
            modules: [],
          });

          await this.loadCurriculums();
          this.selectedCurriculumId = curriculum.id;

          this.$buefy.toast.open({
            message: "Curriculum created successfully!",
            type: "is-success",
          });
        }

        this.isLoading = false;
        this.isModalActive = false;
        this.resetForm();
      } catch (error) {
        this.$buefy.toast.open({
          message: `Failed to ${
            this.isEditing ? "update" : "create"
          } curriculum: ${error.message}`,
          type: "is-danger",
        });
        this.isLoading = false;
      }
    },

    async saveCurriculum() {
      if (!this.selectedCurriculum) return;

      try {
        // Clean up lessons
        const cleanLessons = this.selectedCurriculum.lessons.filter(
          (lesson) => lesson.topicId
        );

        await CurriculumService.updateCurriculum(this.selectedCurriculum.id, {
          lessons: cleanLessons,
          modules: this.selectedCurriculum.modules,
        });

        await this.loadCurriculums();

        this.$buefy.toast.open({
          message: "Curriculum saved successfully!",
          type: "is-success",
        });
      } catch (error) {
        this.$buefy.toast.open({
          message: `Failed to save curriculum: ${error.message}`,
          type: "is-danger",
        });
      }
    },

    /*
     * LessonEditor handlers
     */

    editLesson(index) {
      const lesson = this.selectedCurriculum.lessons[index];
      if (!lesson) return;

      this.lessonToEdit = {
        topicId: lesson.topicId,
        isOptional: lesson.isOptional,
      };
      this.isLessonEditorActive = true;
    },

    closeLessonEditor() {
      this.isLessonEditorActive = false;
      this.lessonToEdit = null;
    },

    handleLessonSaved({ topicId, isOptional }) {
      if (!this.selectedCurriculum?.lessons) return;

      const lesson = this.selectedCurriculum.lessons?.find(
        (l) => l.topicId === topicId
      );
      if (!lesson) return;

      lesson.isOptional = isOptional;

      this.closeLessonEditor();
    },

    /*
     * TopicsColumn handlers
     */

    handleTopicsDrop(event) {
      try {
        const topicData = JSON.parse(event.dataTransfer.getData("text/plain"));
        if (!this.selectedCurriculum?.lessons) return;

        this.selectedCurriculum.removeTopic(topicData.id);

        this.$buefy.toast.open({
          message: `Topic "${topicData.title}" removed from curriculum`,
          type: "is-success",
        });
      } catch (error) {
        console.error("Drop handling error:", error);
        this.$buefy.toast.open({
          message: "Failed to remove topic",
          type: "is-danger",
        });
      }
    },

    /*
     * LessonsColumn handlers
     */

    handleLessonDrop({ topicId, index }) {
      try {
        this.selectedCurriculum.addLessonAtIndex(topicId, index);

        const topic = this.getTopicById(topicId);
        this.$buefy.toast.open({
          message: `Topic "${topic.title}" added as a lesson`,
          type: "is-success",
        });
      } catch (error) {
        console.error("Drop handling error:", error);
        this.$buefy.toast.open({
          message: "Failed to add lesson",
          type: "is-danger",
        });
      }
    },

    handleLessonClear(index) {
      this.selectedCurriculum.clearLessonAtIndex(index);
    },

    handleLessonFull({ topicId, index }) {
      this.selectedCurriculum.addLessonAtIndex(topicId, index);
    },

    handleReorderLessons() {
      if (!this.selectedCurriculum?.lessons) return;
      this.selectedCurriculum.removeEmptyLessons();
    },

    /*
     * ModulesColumn handlers
     */

    handleModuleDrop({ topicId, index }) {
      if (!this.selectedCurriculum) return;

      try {
        const topic = this.getTopicById(topicId);
        this.selectedCurriculum.addTopicToModule(topicId, index);

        this.$buefy.toast.open({
          message: `Topic "${topic.title}" added to module ${index + 1}`,
          type: "is-success",
        });
      } catch (error) {
        console.error("Drop handling error:", error);
        this.$buefy.toast.open({
          message: "Failed to add topic to module",
          type: "is-danger",
        });
      }
    },

    handleModuleClear(index) {
      this.selectedCurriculum?.clearModuleAtIndex(index);
    },

    handleRemoveTopic(topicId) {
      this.selectedCurriculum?.removeTopic(topicId);
    },

    handleModuleFull({ index }) {
      this.$buefy.toast.open({
        message: `Week ${index + 1} is full`,
        type: "is-danger",
      });
    },

    handleReorderModules() {
      if (!this.selectedCurriculum?.modules) return;
      this.selectedCurriculum.reorderModules();
    },
  },
};
</script>

<style scoped>
.curriculum-columns {
  gap: 1rem;
  margin: 0;
  height: 85vh;
}

.curriculum-columns > div {
  border: 4px solid #dbdbdb;
  /* border-radius: 6px; */
  padding: 0;
  margin: 0 !important; /* Override Bulma column margins */
}

/* Ensure columns have equal width */
.curriculum-columns > div.column {
  flex: 1;
}

.module-column,
.topic-column {
  max-width: 400px;
}

.drop-zone.is-dragover {
  background-color: rgba(0, 209, 178, 0.1);
  border: 4px dashed #00d1b2;
}
</style>
